import { useEffect, useRef, useState } from "react";

const WHATSAPP_NUMBER = "233508333537"; // Ghana format for 0508333537
const HOTLINE_NUMBER = "0307000600";

export default function FloatingSupportWidget() {
  const widgetRef = useRef(null);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
    moved: false,
  });

  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });

  useEffect(() => {
    const savedPosition = localStorage.getItem("support-widget-position");

    if (savedPosition) {
      try {
        const parsedPosition = JSON.parse(savedPosition);

        setPosition({
          x: Math.min(parsedPosition.x, window.innerWidth - 70),
          y: Math.min(parsedPosition.y, window.innerHeight - 70),
        });
      } catch {
        setDefaultPosition();
      }
    } else {
      setDefaultPosition();
    }

    setMounted(true);
  }, []);

  useEffect(() => {
    function handleResize() {
      setPosition((current) => ({
        x: Math.max(10, Math.min(current.x, window.innerWidth - 70)),
        y: Math.max(10, Math.min(current.y, window.innerHeight - 70)),
      }));
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  function setDefaultPosition() {
    setPosition({
      x: window.innerWidth - 90,
      y: window.innerHeight - 110,
    });
  }

  function handlePointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;

    event.currentTarget.setPointerCapture(event.pointerId);

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialX: position.x,
      initialY: position.y,
      moved: false,
    };

    setIsDragging(true);
  }

  function handlePointerMove(event) {
    if (!isDragging) return;

    const movementX = event.clientX - dragRef.current.startX;
    const movementY = event.clientY - dragRef.current.startY;

    if (Math.abs(movementX) > 5 || Math.abs(movementY) > 5) {
      dragRef.current.moved = true;
      setIsOpen(false);
    }

    const widgetWidth = widgetRef.current?.offsetWidth || 64;
    const widgetHeight = widgetRef.current?.offsetHeight || 64;

    const nextX = Math.max(
      10,
      Math.min(
        dragRef.current.initialX + movementX,
        window.innerWidth - widgetWidth - 10,
      ),
    );

    const nextY = Math.max(
      10,
      Math.min(
        dragRef.current.initialY + movementY,
        window.innerHeight - widgetHeight - 10,
      ),
    );

    setPosition({ x: nextX, y: nextY });
  }

  function handlePointerUp(event) {
    if (!isDragging) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);

    localStorage.setItem("support-widget-position", JSON.stringify(position));

    if (!dragRef.current.moved) {
      setIsOpen((current) => !current);
    }
  }

  function openWhatsApp() {
    const message = encodeURIComponent("Hello, I need some assistance.");

    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  function callHotline() {
    window.location.href = `tel:${HOTLINE_NUMBER}`;
  }

  const openOnLeft = mounted && position.x > window.innerWidth / 2;

  if (!mounted) return null;

  return (
    <>
      <div
        ref={widgetRef}
        className={`support-widget ${isDragging ? "dragging" : ""}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div
          className={`support-menu ${
            isOpen ? "support-menu-open" : ""
          } ${openOnLeft ? "menu-left" : "menu-right"}`}
        >
          <div className="support-heading">
            <strong>Need help?</strong>
            <span>Contact our support team</span>
          </div>

          <button
            type="button"
            className="support-option whatsapp-option"
            onClick={openWhatsApp}
          >
            <span className="option-icon">
              <WhatsAppIcon />
            </span>

            <span className="option-content">
              <strong>WhatsApp</strong>
              <span>050 833 3537</span>
            </span>
          </button>

          <button
            type="button"
            className="support-option hotline-option"
            onClick={callHotline}
          >
            <span className="option-icon">
              <PhoneIcon />
            </span>

            <span className="option-content">
              <strong>Hotline</strong>
              <span>030 700 0600</span>
            </span>
          </button>
        </div>

        <button
          type="button"
          className={`support-main-button ${isOpen ? "active" : ""}`}
          aria-label={isOpen ? "Close support menu" : "Open support menu"}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {isOpen ? <CloseIcon /> : <SupportIcon />}

          {!isOpen && <span className="support-pulse" />}
        </button>
      </div>

      <style>{`
        .support-widget {
          position: fixed;
          z-index: 9999;
          width: 64px;
          height: 64px;
          user-select: none;
          touch-action: none;
        }

        .support-widget.dragging {
          cursor: grabbing;
        }

        .support-main-button {
          position: relative;
          width: 64px;
          height: 64px;
          border: none;
          border-radius: 50%;
          background: linear-gradient(135deg, #009677, #60b848);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: grab;
          box-shadow: 0 12px 30px rgba(0, 150, 119, 0.35);
          transition:
            transform 0.25s ease,
            background 0.25s ease,
            box-shadow 0.25s ease;
        }

        .support-main-button:hover {
          transform: scale(1.07);
          box-shadow: 0 16px 36px rgba(0, 150, 119, 0.42);
        }

        .support-main-button:active {
          cursor: grabbing;
          transform: scale(0.96);
        }

        .support-main-button.active {
          background: linear-gradient(135deg, #009677, #60b848);
          transform: rotate(90deg);
        }

        .support-main-button svg {
          width: 29px;
          height: 29px;
          pointer-events: none;
        }

        .support-pulse {
          position: absolute;
          inset: -6px;
          border: 2px solid rgba(0, 150, 119, 0.45);
          border-radius: 50%;
          animation: supportPulse 2s infinite;
          pointer-events: none;
        }

        .support-menu {
          position: absolute;
          bottom: 78px;
          width: 280px;
          padding: 12px;
          border: 1px solid rgba(148, 163, 184, 0.25);
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 22px 55px rgba(15, 23, 42, 0.22);
          opacity: 0;
          visibility: hidden;
          transform: translateY(18px) scale(0.92);
          transform-origin: bottom;
          transition:
            opacity 0.35s ease,
            transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
            visibility 0.35s;
          pointer-events: none;
        }

        .support-menu.menu-left {
          right: 0;
          transform-origin: bottom right;
        }

        .support-menu.menu-right {
          left: 0;
          transform-origin: bottom left;
        }

        .support-menu-open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }

        .support-heading {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 7px 8px 13px;
          color: #0f172a;
        }

        .support-heading strong {
          font-size: 16px;
        }

        .support-heading span {
          color: #64748b;
          font-size: 13px;
        }

        .support-option {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          margin-top: 7px;
          border: none;
          border-radius: 13px;
          background: #f8fafc;
          text-align: left;
          cursor: pointer;
          transition:
            transform 0.2s ease,
            background 0.2s ease;
        }

        .support-option:hover {
          transform: translateX(3px);
          background: #f1f5f9;
        }

        .option-icon {
          width: 43px;
          height: 43px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: white;
        }

        .option-icon svg {
          width: 22px;
          height: 22px;
        }

        .whatsapp-option .option-icon {
          background: #25d366;
        }

        .hotline-option .option-icon {
          background: linear-gradient(135deg, #009677, #60b848);
        }

        .option-content {
          display: flex;
          flex-direction: column;
          gap: 3px;
          color: #0f172a;
        }

        .option-content strong {
          font-size: 14px;
        }

        .option-content span {
          color: #64748b;
          font-size: 13px;
        }

        @keyframes supportPulse {
          0% {
            transform: scale(0.9);
            opacity: 0.8;
          }

          70% {
            transform: scale(1.25);
            opacity: 0;
          }

          100% {
            transform: scale(1.25);
            opacity: 0;
          }
        }

        @media (max-width: 480px) {
          .support-menu {
            width: min(280px, calc(100vw - 30px));
          }
        }
      `}</style>
    </>
  );
}

function SupportIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      <path d="M8 10h.01" />
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2a9.84 9.84 0 0 0-8.49 14.8L2 22l5.35-1.51A9.95 9.95 0 1 0 12.04 2Zm0 17.91a8.04 8.04 0 0 1-4.1-1.12l-.29-.17-3.18.9.85-3.1-.19-.31a7.91 7.91 0 1 1 6.91 3.8Zm4.35-5.93c-.24-.12-1.41-.69-1.63-.77-.22-.08-.38-.12-.54.12-.16.24-.62.77-.76.93-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.93-1.19a7.22 7.22 0 0 1-1.34-1.67c-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.19-.47-.39-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.51.1.46-.07 1.41-.58 1.61-1.13.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.78 19.78 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.78 19.78 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
